import React, { useRef, useState, useEffect } from "react";
import {
  IoAlertCircleOutline,
  IoCloseOutline,
  IoRefreshOutline,
  IoTrashOutline,
  IoSyncOutline,
  IoWarningOutline,
} from "react-icons/io5";
import {
  machineAccountsApi,
  MachineAccount,
  ApiKeyInfo,
  MachineAccountService,
} from "../../services/machineAccountsApi";
import ApiKeyModal from "./ApiKeyModal";
import Spinner from "../common/Spinner";
import { useReadOnly } from "../../hooks/useReadOnly";

const AlertIcon = IoAlertCircleOutline as any;
const CloseIcon = IoCloseOutline as any;
const RefreshIcon = IoRefreshOutline as any;
const TrashIcon = IoTrashOutline as any;
const SyncIcon = IoSyncOutline as any;
const WarningIcon = IoWarningOutline as any;

const SERVICE_OPTIONS: Array<{
  value: MachineAccountService;
  title: string;
  tagline: string;
  description: string;
}> = [
  {
    value: "storage",
    title: "Telcofy API",
    tagline: "Most common",
    description:
      "Call the Telcofy API from your own code: run analyses, check which data is delivered, and download your result files.",
  },
  {
    value: "bigquery",
    title: "BigQuery (SQL)",
    tagline: "For data teams",
    description:
      "Query your data directly with SQL: in BigQuery, from Python or R, or in BI tools such as Looker Studio, Power BI or Tableau.",
  },
];

// Intent-based label for a raw service value, so GCP internals
// ("storage" / "bigquery") never surface in user-facing copy.
const serviceTitle = (value: MachineAccountService | null): string =>
  SERVICE_OPTIONS.find((o) => o.value === value)?.title ?? "";

const ServiceBadge: React.FC<{ service?: MachineAccountService }> = ({
  service,
}) => {
  const isBQ = service === "bigquery";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider ${
        isBQ
          ? "border-note-line bg-note-soft text-note-ink"
          : "border-paper-300 bg-paper-100 text-ink-soft"
      }`}
    >
      {isBQ ? "BigQuery" : "API"}
    </span>
  );
};

const ApiKeysManager: React.FC = () => {
  const [keyLabel, setKeyLabel] = useState("");
  const [service, setService] = useState<MachineAccountService>("storage");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newMachineAccount, setNewMachineAccount] =
    useState<MachineAccount | null>(null);

  const [apiKeys, setApiKeys] = useState<ApiKeyInfo[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Keys being deleted right now. Each delete runs on its own, so you can delete
  // several in a row without waiting for the first to finish.
  const [deletingLabels, setDeletingLabels] = useState<Set<string>>(new Set());
  // Read-only (a viewer, or Telcofy support view): keys are listed but can't be
  // created, rotated or deleted. The backend refuses those anyway.
  const { readOnly } = useReadOnly();

  // When the form is pre-loaded from an existing key (rotation flow), we
  // remember the original label + service so we can render the right CTA
  // copy and warn when the service is being changed.
  const [rotatingFromLabel, setRotatingFromLabel] = useState<string | null>(
    null
  );
  const [rotatingFromService, setRotatingFromService] =
    useState<MachineAccountService | null>(null);

  const formRef = useRef<HTMLDivElement | null>(null);

  // Only creating or rotating holds the form; deletes never block anything else.
  const isOperationPending = loading;
  const isRotation =
    rotatingFromLabel !== null &&
    rotatingFromLabel === keyLabel.trim() &&
    rotatingFromService !== null;
  const serviceChangedDuringRotation =
    isRotation && rotatingFromService !== service;

  const resetForm = () => {
    setKeyLabel("");
    setService("storage");
    setRotatingFromLabel(null);
    setRotatingFromService(null);
  };

  const cancelRotation = () => {
    resetForm();
    setError(null);
  };

  const fetchApiKeys = async (quiet = false) => {
    try {
      if (!quiet) setListLoading(true);
      setListError(null);
      const result = await machineAccountsApi.listApiKeys();
      const sortedKeys = [...(result.accounts ?? [])].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setApiKeys(sortedKeys);
    } catch (err) {
      console.error("Failed to fetch API keys:", err);
      setListError(err instanceof Error ? err.message : "Failed to fetch keys");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleCreateKey = async () => {
    const label = keyLabel.trim();
    if (!label) {
      setError("Key label is required");
      return;
    }
    if (isOperationPending) return;

    // For BigQuery keys, derive the dataset name from the keyLabel by
    // stripping every non-letter character, and always provision the
    // realtime_data view. (Per backend spec — see Trello #360 comment.)
    const payload =
      service === "bigquery"
        ? {
            keyLabel: label,
            service,
            createDataset: label.replace(/[^a-zA-Z]/g, ""),
            createRealtimeDataView: true,
          }
        : {
            keyLabel: label,
            service,
          };

    try {
      setLoading(true);
      setError(null);
      const result = await machineAccountsApi.createOrRotateApiKey(payload);
      setNewMachineAccount(result);
      resetForm();
      await fetchApiKeys();
    } catch (err) {
      console.error("Failed to create API key:", err);
      setError(err instanceof Error ? err.message : "Failed to create API key");
    } finally {
      setLoading(false);
    }
  };

  const handlePrepareRotation = (key: ApiKeyInfo) => {
    if (isOperationPending) return;

    const existingService: MachineAccountService =
      key.service === "bigquery" ? "bigquery" : "storage";

    setKeyLabel(key.keyLabel);
    setService(existingService);
    setRotatingFromLabel(key.keyLabel);
    setRotatingFromService(existingService);
    setError(null);

    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDeleteKey = async (label: string) => {
    if (deletingLabels.has(label) || rotatingFromLabel === label) return;

    if (
      !window.confirm(
        `Are you sure you want to delete the key "${label}"?\n\nThis action is permanent and cannot be undone.`
      )
    ) {
      return;
    }

    const mark = (on: boolean) =>
      setDeletingLabels((prev) => {
        const next = new Set(prev);
        if (on) next.add(label);
        else next.delete(label);
        return next;
      });
    try {
      mark(true);
      setError(null);
      await machineAccountsApi.deleteApiKey({ keyLabel: label });
      // Gone: drop it from the list straight away rather than reloading the whole list.
      setApiKeys((keys) => keys.filter((k) => k.keyLabel !== label));
    } catch (err) {
      console.error("Failed to delete API key:", err);
      setError(err instanceof Error ? `Couldn't delete "${label}": ${err.message}` : `Couldn't delete "${label}"`);
      fetchApiKeys(true);
    } finally {
      mark(false);
    }
  };

  const handleCloseModal = () => {
    setNewMachineAccount(null);
  };

  const showCreator = apiKeys.some((k) => k.createdByName);

  const renderKeyList = () => {
    if (listLoading) {
      return (
        <div className="py-10">
          <Spinner />
        </div>
      );
    }

    if (listError) {
      return (
        <div className="flex items-start gap-3 rounded-2xl bg-bad-soft border border-bad-line p-4 text-bad-ink">
          <AlertIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm">Error loading keys: {listError}</span>
        </div>
      );
    }

    if (apiKeys.length === 0) {
      return (
        <div className="rounded-2xl border border-dashed border-paper-300 bg-paper-100 py-10 text-center">
          <p className="text-sm text-ink-muted">No API keys created yet.</p>
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-2xl border border-paper-300/70">
        <table className="w-full">
          <thead className="bg-paper-100">
            <tr>
              <th className="px-5 py-3 text-left text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                Key Label
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                Service
              </th>
              {showCreator && (
                <th className="px-5 py-3 text-left text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                  Created By
                </th>
              )}
              <th className="px-5 py-3 text-left text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                Last Created / Rotated
              </th>
              {!readOnly && (
                <th className="px-5 py-3 text-right text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((key) => {
              const isDeleting = deletingLabels.has(key.keyLabel);
              const isPreppedForRotation = rotatingFromLabel === key.keyLabel;

              return (
                <tr
                  key={key.keyLabel}
                  className="border-t border-paper-200 hover:bg-paper-100 transition-colors"
                >
                  <td className="px-5 py-3 font-mono text-sm text-ink">
                    {key.keyLabel}
                  </td>
                  <td className="px-5 py-3">
                    <ServiceBadge service={key.service} />
                  </td>
                  {showCreator && (
                    <td className="px-5 py-3 text-sm text-ink-muted">{key.createdByName ?? "—"}</td>
                  )}
                  <td className="px-5 py-3 text-sm text-ink-muted">
                    {new Date(key.updatedAt).toLocaleString()}
                  </td>
                  {!readOnly && <td className="px-5 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handlePrepareRotation(key)}
                        disabled={isOperationPending || isDeleting}
                        title="Pre-fill the form to rotate this key"
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                          isPreppedForRotation
                            ? "border-warn bg-warn-soft text-warn-ink ring-1 ring-warn"
                            : "border-warn-line bg-warn-soft text-warn-ink hover:border-warn"
                        }`}
                      >
                        <SyncIcon className="h-3.5 w-3.5" />
                        {isPreppedForRotation ? "Editing…" : "Rotate"}
                      </button>
                      <button
                        onClick={() => handleDeleteKey(key.keyLabel)}
                        disabled={isDeleting || isPreppedForRotation}
                        title="Delete key"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-bad-soft hover:text-bad-ink transition-colors disabled:opacity-50"
                      >
                        {isDeleting ? (
                          <div className="tf-spinner h-3.5 w-3.5 border-2" />
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="mb-8">
        <p className="text-xs font-mono uppercase tracking-widest text-ink-faint mb-3">
          Developer
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-ink mb-3">
          API Keys
        </h1>
        <p className="text-lg text-ink-muted">
          Create and manage API keys to access your data programmatically.
        </p>
      </div>

      {readOnly && (
        <div className="mb-6 rounded-2xl border border-paper-300/70 bg-paper-100 px-5 py-4 text-sm text-ink-muted">
          These are your organisation's keys. Only its editors can create, rotate or delete them.
        </div>
      )}

      {/* Create New Key Card */}
      {!readOnly && <div
        ref={formRef}
        className="mb-6 rounded-3xl border border-paper-300/70 bg-surface p-6 shadow-soft"
      >
        <h2 className="font-display text-xl font-bold text-ink mb-1">
          {isRotation ? "Rotate API key" : "Create or rotate an API key"}
        </h2>
        <p className="text-sm text-ink-muted mb-5">
          {isRotation ? (
            <>
              You're rotating{" "}
              <span className="font-mono text-ink">{rotatingFromLabel}</span>.
              Confirm the service and dataset options below, then click{" "}
              <span className="font-semibold">Rotate Key</span>. The old key
              will be invalidated.
            </>
          ) : (
            <>
              Generate a new key for machine-to-machine authentication. Each key
              should have a unique label (e.g.{" "}
              <span className="font-mono text-ink">production</span>,{" "}
              <span className="font-mono text-ink">etl-dev</span>,{" "}
              <span className="font-mono text-ink">airflow</span>).
            </>
          )}
        </p>

        {isRotation && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-warn-line bg-warn-soft px-4 py-3">
            <div className="flex items-start gap-2 text-sm text-warn-ink">
              <SyncIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span>
                Rotating <span className="font-mono">{rotatingFromLabel}</span>{" "}
                — fields are pre-filled from the existing key.
              </span>
            </div>
            <button
              type="button"
              onClick={cancelRotation}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-warn-ink hover:bg-warn-line/40 transition-colors"
            >
              <CloseIcon className="h-3.5 w-3.5" />
              Cancel
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-xl bg-bad-soft border border-bad-line px-4 py-3 text-sm text-bad-ink">
            <AlertIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-5">
          <div>
            <label
              htmlFor="api-key-label"
              className="block text-sm font-medium text-ink-soft mb-1.5"
            >
              Key Label
            </label>
            <input
              id="api-key-label"
              type="text"
              placeholder="e.g., production, etl-dev, airflow"
              value={keyLabel}
              onChange={(e) => setKeyLabel(e.target.value)}
              className="w-full rounded-xl border border-paper-300 bg-surface px-4 py-2.5 text-sm placeholder:text-ink-faint focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 transition disabled:opacity-50"
              disabled={isOperationPending}
            />
            {!isRotation && (
              <p className="mt-2 text-xs text-ink-faint">
                If you use an existing label, the old key will be rotated
                (invalidated).
              </p>
            )}
          </div>

          <div>
            <span className="block text-sm font-medium text-ink-soft mb-2">
              What will you use this key for?
            </span>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {SERVICE_OPTIONS.map((option) => {
                const isSelected = service === option.value;
                return (
                  <label
                    key={option.value}
                    className={`relative flex cursor-pointer flex-col gap-1.5 rounded-2xl border p-4 transition ${
                      isSelected
                        ? "border-brand-500 bg-brand-50/40 ring-2 ring-brand-500/30"
                        : "border-paper-300 bg-surface hover:bg-paper-100"
                    } ${isOperationPending ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <input
                      type="radio"
                      name="api-key-service"
                      value={option.value}
                      checked={isSelected}
                      onChange={() => setService(option.value)}
                      disabled={isOperationPending}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-ink">
                        {option.title}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${
                          isSelected
                            ? "bg-brand-500 text-on-brand"
                            : "bg-paper-200 text-ink-faint"
                        }`}
                      >
                        {option.tagline}
                      </span>
                    </div>
                    <p className="text-xs text-ink-muted leading-relaxed">
                      {option.description}
                    </p>
                  </label>
                );
              })}
            </div>
            {serviceChangedDuringRotation && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-warn-line bg-warn-soft px-4 py-3 text-sm text-warn-ink">
                <WarningIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>
                  You're changing what this key can do — from{" "}
                  <span className="font-semibold">
                    {serviceTitle(rotatingFromService)}
                  </span>{" "}
                  to <span className="font-semibold">{serviceTitle(service)}</span>.
                  The rotated key will be able to do different things than the
                  one it replaces.
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              onClick={handleCreateKey}
              disabled={isOperationPending || !keyLabel.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-on-brand shadow-brand-glow transition-all hover:bg-brand-600 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {loading && (
                <div className="tf-spinner h-4 w-4 border-2 border-on-brand/30 border-t-on-brand" />
              )}
              {loading
                ? isRotation
                  ? "Rotating…"
                  : "Creating…"
                : isRotation
                  ? "Rotate Key"
                  : "Create / Rotate Key"}
            </button>
          </div>
        </div>
      </div>}

      {/* Existing API Keys Card */}
      <div className="rounded-3xl border border-paper-300/70 bg-surface p-6 shadow-soft">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold text-ink">
            Existing API Keys
          </h2>
          <button
            onClick={() => fetchApiKeys()}
            disabled={listLoading}
            title="Refresh list"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-muted hover:bg-paper-200 transition-colors disabled:opacity-50"
          >
            <RefreshIcon
              className={`h-5 w-5 ${listLoading ? "animate-spin" : ""}`}
            />
          </button>
        </div>
        {renderKeyList()}
      </div>

      {/* API Key Modal */}
      {newMachineAccount && (
        <ApiKeyModal
          apiKey={newMachineAccount.apiKey}
          keyLabel={newMachineAccount.keyLabel}
          serviceAccountEmail={newMachineAccount.serviceAccountEmail}
          service={newMachineAccount.service}
          personalDatasetId={newMachineAccount.personalDatasetId}
          realtimeViewId={newMachineAccount.realtimeViewId}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default ApiKeysManager;
